import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const PORTRAIT = "/img/jess-portrait.jpg";

const Reclaim = () => (
  <main className="bg-[#F6F1EA]" data-testid="reclaim-page">
    {/* Hero */}
    <section className="pt-28 pb-14 sm:pt-32">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
        <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-6">Stage 02 · A private session</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#4F5D4F] leading-[1.05] tracking-tight uppercase" data-testid="reclaim-heading">
          Reclaim
        </h1>
        <p className="mt-5 font-serif italic text-xl sm:text-2xl text-[#B89B63]">Hear your own voice.</p>
      </div>
    </section>

    {/* Copy */}
    <section className="pb-14">
      <div className="max-w-2xl mx-auto px-6 space-y-5 text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-[1.75]">
        <p>Maybe there's one thing you can't stop circling.</p>
        <ul className="font-serif italic text-lg text-[#4F5D4F] space-y-1 pl-1">
          <li>A question.</li>
          <li>A decision.</li>
          <li>A belief.</li>
          <li>A relationship.</li>
          <li>A frustrating experience you keep replaying.</li>
        </ul>
        <p>You've thought about it from every angle, but you still can't seem to hear yourself clearly.</p>
        <p className="font-serif italic text-xl text-[#4F5D4F]">Bring it here.</p>
      </div>
    </section>

    {/* Offer + Pricing */}
    <section className="py-14 sm:py-16 bg-[#D7B2A3]/15">
      <div className="max-w-2xl mx-auto px-6">
        <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">The Session</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] mb-4 tracking-tight">Reclaiming You</h2>
        <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed mb-4">
          A private 60-minute Faith &amp; Identity Coaching session.
        </p>
        <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed mb-4">
          Together, we'll slow it down, get curious about what's underneath it, and untangle
          the voices, fears, expectations, and beliefs surrounding it.
        </p>
        <p className="font-serif italic text-lg text-[#4F5D4F] mb-8">
          This is not a session where I give you the "right" answer. It's a space for you to hear your own.
        </p>

        <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">You'll leave with</p>
        <ul className="space-y-2 text-base text-[#4F5D4F]/90 font-light leading-relaxed mb-8">
          <li>· Greater clarity around what's actually happening</li>
          <li>· Weight lifted after sharing the hard</li>
          <li>· One meaningful insight</li>
          <li>· One practical next step</li>
        </ul>
        <p className="text-sm text-[#8C9A88] italic mb-10">
          You don't need to choose a theological label, disclose your questions to anyone, make a
          major life decision, or have everything figured out.
        </p>

        {/* Pricing */}
        <div className="bg-white border border-[#D7B2A3]/60 p-8 text-center">
          <p className="text-xs tracking-eyebrow uppercase text-[#8C9A88] mb-3">Investment</p>
          <div className="flex items-baseline justify-center gap-3 mb-4" data-testid="reclaim-pricing">
            <span className="font-serif text-2xl text-[#8C9A88] line-through">$250</span>
            <span className="font-serif text-5xl text-[#4F5D4F]">$125</span>
          </div>
          <p className="text-sm text-[#4F5D4F]/70 font-light mb-8">60-minute private session · One-time</p>
          <Link
            to="/booking?package=individual"
            className="group inline-flex items-center gap-3 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-8 py-4 text-xs tracking-eyebrow uppercase transition-colors"
            data-testid="reclaim-book-cta"
          >
            Book Your Session <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>

    {/* About Jess */}
    <section className="py-14 sm:py-16">
      <div className="max-w-3xl mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-5">
          <div className="relative aspect-[3/4] max-w-[240px] mx-auto md:mx-0">
            <div className="absolute -top-3 -left-3 w-16 h-16 bg-[#D7B2A3]/60 z-0" />
            <img src={PORTRAIT} alt="Jess Eisaman" className="relative z-10 w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-7">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">Who's guiding this</p>
          <h2 className="font-serif text-3xl text-[#4F5D4F] leading-tight tracking-tight mb-5">
            Hi, I'm <em className="italic text-[#B89B63]">Jess</em>.
          </h2>
          <p className="text-base text-[#4F5D4F]/85 font-light leading-relaxed">
            I'm a Faith &amp; Identity Coach, seminary graduate, former Christian counselor, and
            ICF-certified coach. I'm here to ask thoughtful questions, offer perspective and
            tools, create space for what comes up, and walk beside you as you discover what is
            true for you.
          </p>
        </div>
      </div>
    </section>
  </main>
);

export default Reclaim;
